import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Wallet } from "lucide-react";
import WhatsAppOpsBindingsPanel from "../../components/WhatsAppOpsBindingsPanel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

export default function WhatsAppOpsAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const API = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [granting, setGranting] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    margin_percent: 5,
    free_credit_eur: 2,
    usd_to_eur_rate: 0.92,
  });
  const [packages, setPackages] = useState([]);
  const [newPkg, setNewPkg] = useState({ name: "", price_eur: "5", description: "" });
  const [pricingSync, setPricingSync] = useState(null);
  const [syncingPricing, setSyncingPricing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/whatsapp-ops/billing`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore caricamento");
      setSettings({
        enabled: json.settings?.enabled !== false,
        margin_percent: json.settings?.margin_percent ?? 5,
        free_credit_eur: json.settings?.free_credit_eur ?? 2,
        usd_to_eur_rate: json.settings?.usd_to_eur_rate ?? 0.92,
      });
      setPackages(json.packages || []);
      setPricingSync(json.pricing_sync || null);
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  const runPricingSync = async () => {
    setSyncingPricing(true);
    try {
      const res = await fetch(`${API}/api/admin/whatsapp-ops/billing/sync-pricing`, {
        method: "POST",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Sync fallita");
      toast.success("Listini modelli e cambio USD→EUR aggiornati");
      if (json.pricing_sync) {
        setPricingSync(json.pricing_sync);
        if (json.pricing_sync.usd_to_eur_rate != null) {
          setSettings((s) => ({ ...s, usd_to_eur_rate: json.pricing_sync.usd_to_eur_rate }));
        }
      }
    } catch (e) {
      toast.error(e.message || "Errore sync");
    } finally {
      setSyncingPricing(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/whatsapp-ops/billing/settings`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          enabled: settings.enabled,
          margin_percent: Number(settings.margin_percent),
          free_credit_eur: Number(settings.free_credit_eur),
          usd_to_eur_rate: Number(settings.usd_to_eur_rate),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore salvataggio");
      toast.success("Impostazioni crediti salvate");
      if (json.settings) setSettings((s) => ({ ...s, ...json.settings }));
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const createPackage = async () => {
    try {
      const res = await fetch(`${API}/api/admin/whatsapp-ops/billing/packages`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: newPkg.name,
          price_eur: Number(newPkg.price_eur),
          description: newPkg.description,
          active: true,
          sort_order: (packages.length + 1) * 10,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore creazione");
      toast.success("Pacchetto creato");
      setNewPkg({ name: "", price_eur: "5", description: "" });
      load();
    } catch (e) {
      toast.error(e.message || "Errore");
    }
  };

  const togglePackage = async (pkg) => {
    try {
      const res = await fetch(`${API}/api/admin/whatsapp-ops/billing/packages/${pkg.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ active: !pkg.active }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore");
      load();
    } catch (e) {
      toast.error(e.message || "Errore");
    }
  };

  const removePackage = async (pkg) => {
    if (!window.confirm(`Eliminare il pacchetto «${pkg.name}»?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/whatsapp-ops/billing/packages/${pkg.id}`, {
        method: "DELETE",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore");
      toast.success("Pacchetto eliminato");
      load();
    } catch (e) {
      toast.error(e.message || "Errore");
    }
  };

  const grantFreeAll = async () => {
    if (
      !window.confirm(
        "Concedere il credito gratuito a tutte le organizzazioni che non lo hanno ancora ricevuto?"
      )
    ) {
      return;
    }
    setGranting(true);
    try {
      const res = await fetch(`${API}/api/admin/whatsapp-ops/billing/grant-free-all`, {
        method: "POST",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore");
      toast.success(
        `Credito gratuito: ${json.granted} organizzazioni aggiornate (${json.skipped} già ok).`
      );
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setGranting(false);
    }
  };

  const effectiveExample = () => {
    const price = 5;
    const margin = Number(settings.margin_percent) || 0;
    const eff = price * (1 - Math.min(95, Math.max(0, margin)) / 100);
    return { price, eff: eff.toFixed(2) };
  };
  const ex = effectiveExample();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">WhatsApp operativo</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Numeri operatori admin, pacchetti prepagati e margine sul credito AI dedicato a
          WhatsApp operativo. Le altre funzioni AI restano gratuite per le strutture.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="w-4 h-4 text-amber-600" />
            Crediti e margine
          </CardTitle>
          <CardDescription>
            Esempio: pacchetto da {ex.price} € con ricarico {settings.margin_percent}% → l&apos;utente
            vede {ex.price} €, il credito effettivo per i costi OpenAI è {ex.eff} €.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label>Addebito credito su WhatsApp operativo</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Se spento, i messaggi non scalano credito (utile in test).
                  </p>
                </div>
                <Switch
                  checked={!!settings.enabled}
                  onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label>Margine ricarico (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={95}
                    step={0.1}
                    className="mt-1"
                    value={settings.margin_percent}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, margin_percent: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Credito gratuito iniziale (€)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.1}
                    className="mt-1"
                    value={settings.free_credit_eur}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, free_credit_eur: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Cambio USD → EUR</Label>
                  <Input
                    type="number"
                    min={0.01}
                    step={0.01}
                    className="mt-1"
                    value={settings.usd_to_eur_rate}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, usd_to_eur_rate: e.target.value }))
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    I costi OpenAI sono in USD; qui li converti in euro da scalare.
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
                <p className="font-medium text-slate-800">Sync automatico listini (giornaliero)</p>
                <p>
                  Ultimo sync:{" "}
                  {pricingSync?.synced_at
                    ? new Date(pricingSync.synced_at).toLocaleString("it-IT")
                    : "mai eseguito (parte ~3 min dopo l'avvio server, poi ogni 24h)"}
                </p>
                <p>
                  Cambio in cache:{" "}
                  <strong>
                    1 USD = {Number(pricingSync?.usd_to_eur_rate ?? settings.usd_to_eur_rate).toFixed(5)} EUR
                  </strong>
                  {" · "}Whisper:{" "}
                  <strong>
                    {Number(pricingSync?.whisper_usd_per_minute ?? 0.006).toFixed(4)} $/min
                  </strong>
                </p>
                {pricingSync?.models && (
                  <ul className="mt-1 space-y-0.5">
                    {Object.entries(pricingSync.models).map(([id, rates]) => (
                      <li key={id}>
                        {id}: input {Number(rates.input).toFixed(2)} / output{" "}
                        {Number(rates.output).toFixed(2)} USD per 1M token
                      </li>
                    ))}
                  </ul>
                )}
                {(pricingSync?.errors || []).length > 0 && (
                  <p className="text-amber-700">
                    Avvisi: {(pricingSync.errors || []).join(" · ")}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" onClick={runPricingSync} disabled={syncingPricing}>
                  {syncingPricing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Aggiorna listini ora
                </Button>
                <Button variant="outline" onClick={grantFreeAll} disabled={granting}>
                  {granting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Concedi credito gratuito a tutte le org
                </Button>
                <Button onClick={saveSettings} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salva impostazioni
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pacchetti prepagati</CardTitle>
          <CardDescription>
            Visibili in Impostazioni → WhatsApp operativo. Il pagamento usa Stripe Checkout
            (oppure modalità demo se Stripe non è configurato).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-4 items-end">
            <div className="sm:col-span-1">
              <Label>Nome</Label>
              <Input
                className="mt-1"
                value={newPkg.name}
                onChange={(e) => setNewPkg((p) => ({ ...p, name: e.target.value }))}
                placeholder="Starter 5 €"
              />
            </div>
            <div>
              <Label>Prezzo €</Label>
              <Input
                className="mt-1"
                type="number"
                min={0.5}
                step={0.5}
                value={newPkg.price_eur}
                onChange={(e) => setNewPkg((p) => ({ ...p, price_eur: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-1">
              <Label>Descrizione</Label>
              <Input
                className="mt-1"
                value={newPkg.description}
                onChange={(e) => setNewPkg((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <Button onClick={createPackage} disabled={!newPkg.name.trim()}>
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi
            </Button>
          </div>

          <ul className="divide-y rounded-lg border">
            {packages.length === 0 ? (
              <li className="p-4 text-sm text-slate-500">Nessun pacchetto. Verranno creati i default al primo caricamento.</li>
            ) : (
              packages.map((pkg) => (
                <li key={pkg.id} className="flex flex-wrap items-center gap-3 p-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">
                      {pkg.name}{" "}
                      <span className="text-emerald-700">{Number(pkg.price_eur).toFixed(2)} €</span>
                    </p>
                    {pkg.description && (
                      <p className="text-xs text-slate-500 truncate">{pkg.description}</p>
                    )}
                  </div>
                  <Switch checked={!!pkg.active} onCheckedChange={() => togglePackage(pkg)} />
                  <Button size="sm" variant="outline" onClick={() => removePackage(pkg)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </li>
              ))
            )}
          </ul>
        </CardContent>
      </Card>

      <WhatsAppOpsBindingsPanel mode="admin" authHeaders={headers} />
    </div>
  );
}
